import { Card, Col, Row, Tooltip, Typography } from "antd"
import React, { type ReactNode } from "react"
import { useTranslation } from "react-i18next"

import { formatDays, formatMoney, formatPercent } from "./format"
import type { TeamPerformanceResponse } from "./types"

const { Text } = Typography

interface KpiProps {
  title: string
  value: ReactNode
  caption: ReactNode
}

function Kpi({ title, value, caption }: KpiProps) {
  return (
    <Card size="small" className="h-full">
      <Text type="secondary">{title}</Text>
      <div className="text-2xl font-semibold leading-tight">{value}</div>
      <Text type="secondary" className="text-xs">
        {caption}
      </Text>
    </Card>
  )
}

interface Props {
  data: TeamPerformanceResponse
}

// Totals over the filtered closed projects, never averages of member averages
// (spec §4 "Dashboard interaction details").
export function PerformanceKpiCards({ data }: Props) {
  const { t } = useTranslation()
  const { summary, amounts_visible: amountsVisible } = data
  const money = (value: number | null) =>
    amountsVisible ? (
      formatMoney(value)
    ) : (
      <Tooltip title={t("teamPerformance.kpi.financeRequired")}>
        <span tabIndex={0}>
          <span aria-hidden="true">—</span>
          <span className="sr-only">
            {t("teamPerformance.kpi.financeRequired")}
          </span>
        </span>
      </Tooltip>
    )

  const cards: KpiProps[] = [
    {
      title: t("teamPerformance.kpi.members"),
      value: summary.members_with_projects,
      caption: t("teamPerformance.kpi.membersCaption")
    },
    {
      title: t("teamPerformance.kpi.closed"),
      value: summary.projects,
      caption: t("teamPerformance.kpi.closedCaption", {
        period: data.filters.period_label
      })
    },
    {
      title: t("teamPerformance.kpi.onTime"),
      value: formatPercent(summary.on_time_percent),
      caption: t("teamPerformance.kpi.onTimeCaption", {
        count: summary.on_time,
        total: summary.schedule_eligible
      })
    },
    {
      title: t("teamPerformance.kpi.avgDelay"),
      value: formatDays(summary.avg_delay_days),
      caption: t("teamPerformance.kpi.avgDelayCaption")
    },
    {
      title: t("teamPerformance.kpi.costOverrun"),
      value: money(summary.overrun_amount),
      caption: t("teamPerformance.kpi.projectsCaption", {
        count: summary.overrun_projects
      })
    },
    {
      title: t("teamPerformance.kpi.revenueGap"),
      value: money(summary.gap_amount),
      caption: t("teamPerformance.kpi.projectsCaption", {
        count: summary.gap_projects
      })
    }
  ]

  return (
    <Row gutter={[12, 12]}>
      {cards.map((card) => (
        <Col key={card.title} xs={12} md={8} xl={4}>
          <Kpi {...card} />
        </Col>
      ))}
    </Row>
  )
}
